'use client';

import { useTranslations } from 'next-intl';
import { type FC, type MouseEvent, useEffect, useId, useState } from 'react';
import { BaseLineEdit } from '@/app/components/icons/BaseLineEdit';
import { ImageWithPlaceholder } from '@/app/components/shared/ImageWithPlaceholder';
import { toast } from '@/app/components/ui/use-toast';
import { USER_BG } from '@/app/constants';
import type { UserInfo } from '@/app/types';
import { cn, getImageSize } from '@/app/utils';
import { detectImageType } from '@/app/utils/client';
import type { UploadFileInfo } from './types';
import { UserAvatarDialog } from './UserAvatarDialog';

const DEFAULT_INITIALS_LENGTH = 2;

/** 纯展示头像：仅根据 nickname、id、url 渲染，无编辑逻辑 */
interface AvatarProps {
  nickname?: string;
  id: string;
  url?: string;
  /** 无 url 时展示的昵称首字符个数，默认 2 */
  initialsLength?: number;
  className?: string;
  nameClassName?: string;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

const DEFAULT_INITIALS = 'Vi';

function getFallbackColor(id: string): string {
  if (!id) return USER_BG[0];
  const index = Number(id.slice(-2)) % USER_BG.length;
  return Number.isNaN(index) ? USER_BG[0] : USER_BG[index];
}

function getInitials(nickname?: string, length: number = DEFAULT_INITIALS_LENGTH): string {
  const len = length > 0 ? length : DEFAULT_INITIALS_LENGTH;
  return nickname?.trim().substring(0, len) || DEFAULT_INITIALS;
}

export const Avatar: FC<AvatarProps> = ({
  nickname,
  id,
  url,
  initialsLength = DEFAULT_INITIALS_LENGTH,
  className,
  nameClassName,
  onClick,
}) => {
  if (url) {
    return (
      <div className={cn('size-9 overflow-hidden rounded-full', className)} onClick={onClick}>
        <ImageWithPlaceholder src={url} alt={nickname ?? ''} />
      </div>
    );
  }

  return (
    <div
      className={cn('size-9 rounded-full text-system-white', className)}
      style={{ backgroundColor: getFallbackColor(id) }}
      onClick={onClick}
    >
      <div className={cn('flex h-full w-full items-center justify-center font-medium', nameClassName)}>
        {getInitials(nickname, initialsLength)}
      </div>
    </div>
  );
};

// --- 可编辑头像相关类型与逻辑 ---

interface UserAvatarProps {
  userInfo: UserInfo;
  className?: string;
  nameClassName?: string;
  editable?: boolean;
  onClick?: (e: MouseEvent<HTMLDivElement>) => void;
}

const ACCEPT_IMAGE_MIMETYPE: string[] = ['image/jpeg', 'image/png', 'image/webp'];

function showError(msg: string) {
  toast({
    type: 'error',
    message: msg,
  });
}

export const UserAvatar: FC<UserAvatarProps> = ({ userInfo, className, editable = false, nameClassName, onClick }) => {
  const [confirmUrl, setConfirmUrl] = useState<string>('');
  const [open, setOpen] = useState(false);
  const avatarId = useId();
  const [fileUploadInfo, setFileUploadInfo] = useState<UploadFileInfo>({
    image_resolution: '',
    pic_type: '',
    is_success: false,
    fail_reason: '',
    upload_time: 0,
  });
  const t = useTranslations();

  const displayUrl = confirmUrl || userInfo.avatar;

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const startUploadTime = performance.now();
    const file = e.target.files?.[0];
    const uploadInfo: UploadFileInfo = {
      image_resolution: '',
      pic_type: '',
      is_success: false,
      fail_reason: '',
      upload_time: 0,
    };

    e.target.value = '';
    if (!file) return;

    if (ACCEPT_IMAGE_MIMETYPE.includes(file.type)) {
      const fileType = await detectImageType(file);
      uploadInfo.pic_type = fileType || '';
      if (!fileType || !ACCEPT_IMAGE_MIMETYPE.includes(fileType)) {
        showError(t('create.createForm.upload.error.incorrectExtension'));
        uploadInfo.fail_reason = 'incorrectExtension';
        return;
      }
    } else {
      showError(t('create.createForm.upload.error.invalidType'));
      uploadInfo.pic_type = file.type;
      uploadInfo.fail_reason = 'invalidType';
      return;
    }

    setOpen(true);
    const imageURL = URL.createObjectURL(file);

    try {
      const { width, height } = await getImageSize(imageURL);
      uploadInfo.image_resolution = `${width}x${height}`;
      uploadInfo.is_success = true;
      uploadInfo.upload_time = performance.now() - startUploadTime;
      setConfirmUrl(imageURL);
      setFileUploadInfo(uploadInfo);
    } catch (error) {
      URL.revokeObjectURL(imageURL);
      console.error('上传头像失败:', error);
      toast({
        type: 'warning',
        message: error as string,
      });
    }
  };

  const handleAvatarClick = (e: React.MouseEvent<HTMLLabelElement>) => {
    if (userInfo.auditing_info?.avatar) {
      e.preventDefault();
      toast({ title: t('personal.bio.auditing') });
      return;
    }
  };

  useEffect(() => {
    return () => {
      if (confirmUrl?.startsWith('blob:')) {
        URL.revokeObjectURL(confirmUrl);
      }
    };
  }, [confirmUrl]);

  const avatarElement = (
    <Avatar
      nickname={userInfo.nickname}
      id={userInfo.id}
      url={displayUrl}
      className={className}
      nameClassName={nameClassName}
      onClick={onClick}
    />
  );

  if (!editable) {
    return avatarElement;
  }

  return (
    <>
      <div className="size-20 md:size-12">
        <label htmlFor={avatarId} className="cursor-pointer" onClick={handleAvatarClick}>
          {avatarElement}
          <div className="absolute bottom-0 left-14 block size-5 rounded-full bg-[rgba(0,0,0,0.60)] p-[2px] md:hidden">
            <BaseLineEdit className="size-4" />
          </div>
          <input id={avatarId} type="file" className="hidden" accept="image/*" onChange={handleFileChange} />
        </label>
      </div>
      <UserAvatarDialog
        open={open}
        confirmUrl={confirmUrl}
        onClose={() => setOpen(false)}
        setUrl={setConfirmUrl}
        fileUploadInfo={fileUploadInfo}
      />
    </>
  );
};
